import AssignIcon from "@assets/icons/assign.svg";
import DeleteIcon from "@assets/icons/delete.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { libraryFilterManager, onboardingManager, templateManager, userStatusManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { ProgramContent, programContentAreEquals, programContentDeepCopy } from "@domain/program/program";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { isTemplate, NewTemplate, Template } from "@domain/template/template";
import { sendAnalyticsEventEditTemplate, sendAnalyticsEventSaveTemplate } from "@domain/template/template-analytics";
import { RegistrationStatus } from "@domain/user/user";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { ToastContext } from "@ui/components/common/toast-provider";
import { TrackingContextProvider } from "@ui/components/common/tracking-context-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { ProgramContentEditorView } from "@ui/pages/program-builder/program-content-editor-view";
import { useObservable } from "micro-observables";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { getStartedManager } from "@core/service-container";
import { ProgramListTabNames } from "@ui/pages/program/components/tabs";

export const TemplateBuilderView: React.FC<{ template: Template | NewTemplate; templateIsLoading: boolean }> = ({
	template,
	templateIsLoading,
}) => {
	const { format } = useI18n();
	const programContentWithEmptyName = useMemo(
		() => ({ name: template.name ?? "", days: template.days }),
		[template.days, template.name],
	);

	// if dataIsLoading change it should not reset the content so there is a useEffect to change editable property if viewOnly change
	const programEditor = useMemo(
		() =>
			new ProgramContentEditor(
				programContentDeepCopy(programContentWithEmptyName),
				onboardingManager,
				templateIsLoading,
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[programContentWithEmptyName],
	);

	const { showToast } = useContext(ToastContext);
	const dialogContext = useContext(DialogContext);

	const navigate = useNavigate();
	const ptStatus = useObservable(userStatusManager.ptRegistrationStatus);
	const preSelectedExercises = useObservable(libraryFilterManager.selectedExercises);
	const [isQuitBlockingEnabled, setIsQuitBlockingEnabled] = useState(true);
	const [hasPendingChanges, setHasPendingChanges] = useState(false);
	const [editedTemplateIsEmpty, setEditedTemplateIsEmpty] = useState(template.days.isEmpty);

	const defaultName = useMemo(() => format("templateBuilder.defaultTitle.v2_1"), [format]);
	const navigateToNewTemplate = useCallback(
		(templateId: string) =>
			navigate(
				RoutesPath.TemplateBuilder,
				{ query: { templateId }, state: { preSelectedExercises } },
				{ replace: true },
			),
		[navigate, preSelectedExercises],
	);

	const isPendingUser = useMemo(() => ptStatus === RegistrationStatus.PENDING, [ptStatus]);

	const [saveTemplate, saveLoading] = useAsyncOperation(
		async (editedContent: ProgramContent) => {
			try {
				setIsQuitBlockingEnabled(false);
				let metadata;
				if (!isTemplate(template)) {
					metadata = await templateManager.createTemplate(editedContent);
					await getStartedManager.tryMarkProgramsStepAsCompleted();
					sendAnalyticsEventSaveTemplate(metadata.id);
				} else {
					await templateManager.updateTemplate(template.id, editedContent);
					sendAnalyticsEventEditTemplate(template.id);
				}
				showToast(
					format("templateBuilder.saveButton.successToast.title"),
					format("templateBuilder.saveButton.successToast.message.v2_6"),
					"success",
				);
				if (!isTemplate(template) && metadata) {
					navigateToNewTemplate(metadata.id);
				}
			} catch (error) {
				console.error("Failed to save template", error);
				showToast(
					format("templateBuilder.saveButton.errorToast.title"),
					format("templateBuilder.saveButton.errorToast.message"),
				);
			} finally {
				setIsQuitBlockingEnabled(true);
			}
		},
		{ deps: [format, navigateToNewTemplate, showToast, template] },
	);

	const availableActions = useMemo(() => {
		if (!isTemplate(template)) {
			return [];
		}

		const deleteAction = {
			text: format("templateBuilder.popupMenu.deleteTemplate"),
			icon: <DeleteIcon width={24} height={24} />,
			destructive: true,
			onClick: () => {
				dialogContext?.openModal(ModalId.DeleteTemplate, {
					templateId: template.id,
					templateName: template.name,
					onSuccess: () => {
						navigate(RoutesPath.ProgramList, {
							query: { active: ProgramListTabNames.Templates },
						});
						dialogContext.closeAll();
					},
				});
			},
		};

		return [deleteAction];
	}, [format, template, dialogContext, navigate]);

	const statusMessage = useMemo(
		() =>
			isTemplate(template)
				? format("templateBuilder.statusMessage")
				: format("templateBuilder.statusMessage.newProgram"),
		[format, template],
	);

	const secondaryButtonLabel = useMemo(
		() =>
			isTemplate(template) ? format("templateBuilder.saveButton") : format("templateBuilder.saveButton.asTemplate"),
		[format, template],
	);

	const primaryButtonDisabledTooltipMessage = useMemo(() => {
		if (isPendingUser) {
			return format("templateBuilder.demoMode.cantAssignTemplate.tooltip");
		}

		if (editedTemplateIsEmpty) {
			return format("templateBuilder.cantAssignTemplateTooltipMessage");
		}

		return format("templateBuilder.cantAssignNotSavedTemplateTooltipMessage");
	}, [editedTemplateIsEmpty, format, isPendingUser]);

	return (
		<TrackingContextProvider category={AnalyticsCategory.TEMPLATE} programId={isTemplate(template) ? template.id : ""}>
			<ProgramContentEditorView
				programContent={programContentWithEmptyName}
				programEditor={programEditor}
				statusMessage={statusMessage}
				availableActions={availableActions}
				dataIsLoading={templateIsLoading}
				onProgramContentChanged={newProgramContent => {
					setHasPendingChanges(!programContentAreEquals(newProgramContent, programContentWithEmptyName));
					setEditedTemplateIsEmpty(newProgramContent.days.isEmpty);
				}}
				secondaryButton={{
					label: secondaryButtonLabel,
					disabled: editedTemplateIsEmpty || !hasPendingChanges,
					disabledTooltipMessage: editedTemplateIsEmpty
						? format("templateBuilder.cantSaveEmptyTemplateTooltipMessage")
						: format("templateBuilder.cantSaveBecauseAlreadySavedTooltipMessage"),
					onClick: (programContent: ProgramContent) => {
						const newTemplate: Template | NewTemplate = { ...template, ...programContent };
						if (!programContent.name) {
							dialogContext?.openModal(ModalId.TemplateName, {
								template: { ...newTemplate, name: newTemplate.name ?? defaultName },
								onSubmit: async (name: string) => {
									await saveTemplate({ ...programContent, name });
								},
							});
						} else {
							saveTemplate(programContent);
						}
					},
					loading: saveLoading,
				}}
				primaryButton={{
					label: format("templateBuilder.assignButton"),
					icon: <AssignIcon className="p-button-icon-right" width={20} height={20} />,
					disabled: editedTemplateIsEmpty,
					disabledTooltipMessage: primaryButtonDisabledTooltipMessage,
					onClick: (programContent: ProgramContent) => {
						const newTemplate: Template = { ...(template as Template), ...programContent };
						setIsQuitBlockingEnabled(false);
						dialogContext?.openModal(ModalId.AssignTemplate, {
							template: newTemplate,
							onCancel: () => setIsQuitBlockingEnabled(true),
						});
					},
				}}
				quitBlockingEnabled={isQuitBlockingEnabled}
			/>
		</TrackingContextProvider>
	);
};
